import { CongestionMarker, Grid, GridItem, Stack, Tag, Text, TextLink } from '@ads-core/components';
import { LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { FollowUpStepsSitecoreProps } from '@ads-core/types';

import { capitalizeLocationName, quarterDateFormatter } from 'src/utils';
import { sortArrayByDate } from 'src/utils/dates/sortArrayByDate';

import { CongestionReportsResponse } from '../hooks/useCongestionReports';
import { CongestionProps } from '..';
import { CONGESTION_DIRECTION_CONSUME, CONGESTION_DIRECTION_SUPPLY } from '../constant';
import { isResolvedCongestion } from './isResolvedCongestion';
import { rewriteName } from './rewriteName';
import { getReportUrls, ReportUrl } from './getReportUrl';
import { getLatestBandWidthDate } from './getLatestBandWidthDate';
import { getLatestCompletionDate } from './getLatestCompletionDate';

type FollowUpStepProps = FollowUpStepsSitecoreProps['followUpSteps'][number];

type SetCongestionFollowUpStepsProps = {
  congestions?: CongestionProps[];
  reports?: CongestionReportsResponse[];
  isLoading?: boolean;
  congestionReportsWildcardPage?: LinkField;
  enableBandWidth?: boolean;
};

// Generated the data for the follow up step cards.
export const setCongestionFollowUpSteps = ({
  congestions,
  reports,
  isLoading,
  congestionReportsWildcardPage,
  enableBandWidth,
}: SetCongestionFollowUpStepsProps): FollowUpStepProps[] => {
  const followUpSteps: FollowUpStepProps[] = [];

  if (isLoading) {
    return [];
  }

  const sortedByLatestEndDate = sortArrayByDate<CongestionProps, 'latestEndDate'>(
    congestions ?? [],
    'latestEndDate'
  );

  // Creates the data for the cards
  sortedByLatestEndDate?.forEach((item) => {
    const consume = isResolvedCongestion(item.level.consume);
    const supply = isResolvedCongestion(item.level.supply);

    const solved = consume && supply;

    // Only add unsolved items to the followUpSteps
    if (!solved) {
      const formatName = item.name ? rewriteName(item.name) : undefined;

      const relatedReports = reports?.filter(
        (report) => report.name?.toLowerCase() === formatName?.toLowerCase()
      );

      const endDate = item.latestEndDate
        ? quarterDateFormatter(item.latestEndDate, 'Onbekend')
        : undefined;

      const latestCompletionDate = getLatestCompletionDate(relatedReports);
      const completionDate = latestCompletionDate
        ? quarterDateFormatter(latestCompletionDate, 'Onbekend')
        : undefined;

      const name = formatName ? capitalizeLocationName(formatName) : undefined;
      const tag = endDate ? `Opgelost (verwacht): ${endDate}` : '';
      const target = congestionReportsWildcardPage?.value?.target;
      const link = congestionReportsWildcardPage?.value?.href;

      const reportUrls = getReportUrls(relatedReports, link, target);
      const bandWidthDate = relatedReports ? getLatestBandWidthDate(relatedReports) : undefined;

      followUpSteps.push({
        title: name ? (
          <Text size="label">{`Knelpunt: ${name}`}</Text>
        ) : (
          <Text size="label">Knelpunt zonder projectnaam</Text>
        ),
        tag: endDate ? <Tag>{tag}</Tag> : undefined,
        type: solved ? 'checked' : undefined,
        container: enableBandWidth
          ? ReportContainer(reportUrls, consume, supply, bandWidthDate, completionDate)
          : null,
      });
    }
  });

  return followUpSteps;
};

const ReportContainer = (
  reportUrls: ReportUrl[] | undefined,
  consume: boolean,
  supply: boolean,
  bandWidthDate: string | undefined,
  completionDate: string | undefined
) => {
  return (
    <Grid columnGap={4} rowGap={6} columns={2}>
      <GridItem columnStart="1" columnEnd="2">
        <CongestionMarker congestion={consume} titel={CONGESTION_DIRECTION_CONSUME} />
      </GridItem>
      <GridItem columnStart="2" columnEnd="-1">
        <CongestionMarker congestion={supply} titel={CONGESTION_DIRECTION_SUPPLY} />
      </GridItem>

      {!reportUrls?.length ? (
        <GridItem columnStart="1" columnEnd="-1">
          <Text size="paragraph">Geen actuele vooraankondiging beschikbaar</Text>
        </GridItem>
      ) : null}

      {bandWidthDate || completionDate ? (
        <>
          <GridItem columnStart="1" columnEnd="2">
            <Stack direction="column" gap={2}>
              <Text size="label">Start:</Text>
              <Text size="description">{bandWidthDate}</Text>
            </Stack>
          </GridItem>
          <GridItem columnStart="2" columnEnd="-1">
            <Stack direction="column" gap={2}>
              <Text size="label">Opgelost:</Text>
              <Text size="description">{completionDate}</Text>
            </Stack>
          </GridItem>
        </>
      ) : null}

      {reportUrls?.length ? (
        <GridItem columnStart="1" columnEnd="-1">
          <Text size="description">
            Wilt u meer informatie over dit knelpunt? Ga naar{' '}
            {reportUrls?.map((reportUrl, index) => (
              <>
                <TextLink
                  href={reportUrl.link}
                  target={reportUrl.target}
                  key={reportUrl.congestionType}
                  fontSize="description"
                >
                  {reportUrl.congestionType.toLowerCase()}
                </TextLink>
                {index !== reportUrls.length - 1 && ' of '}
              </>
            ))}{' '}
            om het rapport te bekijken.
          </Text>
        </GridItem>
      ) : null}
    </Grid>
  );
};
